// src/pages/admin/Posts/Posts.js
import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Modal, Form, message, Space, Select, DatePicker, InputNumber } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment'; // For handling dates; consider switching to dayjs

const { Option } = Select;

const Posts = ({ onLogout }) => { // Added onLogout prop
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [form] = Form.useForm();

  // Initial posts data (replace with API fetch in production)
  const initialPosts = [
    {
      id: 1,
      title: 'AI Revolution in Africa',
      date: '2025-02-10',
      description: 'How AI is transforming industries across Africa...',
      tags: ['Artificial Intelligence'],
      image: 'https://www.jecthub.com/images/ai-ml.jpeg',
      views: 1200,
      status: 'Active',
    },
    {
      id: 2,
      title: 'Future of Cybersecurity',
      date: '2025-01-28',
      description: 'Key trends and innovations in cybersecurity for 2025...',
      tags: ['Cybersecurity'],
      image: 'https://www.jecthub.com/static/media/slider1.0cced2a20e757b6027db.jpg',
      views: 950,
      status: 'Active',
    },
    {
      id: 3,
      title: 'Big Data and You',
      date: '2024-12-15',
      description: 'Understanding the impact of big data in decision-making...',
      tags: ['Data Science'],
      image: 'https://www.jecthub.com/images/data-science.jpeg',
      views: 870,
      status: 'Active',
    },
  ];

  const tagOptions = [
    'Artificial Intelligence',
    'Cybersecurity',
    'Data Science',
    'Machine Learning',
    'Deep Learning',
    'IoT',
    'Environment',
    'Ethics',
    'Quantum Computing',
    'Finance',
    '5G',
    'Threats',
    'Sports',
    'Smart Cities',
    'Workplace',
    'Education',
    'Blockchain',
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      setPosts(initialPosts);
      setFilteredPosts(initialPosts);
      setLoading(false);
    }, 500);

    // Placeholder for real API call
    /*
    fetch('/api/posts')
      .then((response) => response.json())
      .then((data) => {
        setPosts(data);
        setFilteredPosts(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching posts:', error);
        setLoading(false);
      });
    */
  }, []);

  const handleSearch = (value) => {
    const filtered = posts.filter(
      (post) =>
        post.title.toLowerCase().includes(value.toLowerCase()) ||
        post.description.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredPosts(filtered);
    setCurrentPage(1);
  };

  const handleAddPost = () => {
    setIsAddModalVisible(true);
  };

  const handleEditPost = (post) => {
    setSelectedPost(post);
    form.setFieldsValue({
      ...post,
      date: moment(post.date), // Convert string to moment for DatePicker
    });
    setIsEditModalVisible(true);
  };

  const handleDeletePost = (postId) => {
    const post = posts.find((p) => p.id === postId);
    Modal.confirm({
      title: 'Delete Post',
      content: `Are you sure you want to delete the post "${post.title}"?`,
      onOk: () => {
        const updatedPosts = posts.filter((p) => p.id !== postId);
        setPosts(updatedPosts);
        setFilteredPosts(updatedPosts);
        message.success(`Post "${post.title}" deleted successfully`);
      },
    });
  };

  const handleToggleStatus = (postId) => {
    const post = posts.find((p) => p.id === postId);
    const newStatus = post.status === 'Active' ? 'Inactive' : 'Active';
    Modal.confirm({
      title: `${newStatus === 'Inactive' ? 'Deactivate' : 'Activate'} Post`,
      content: `Are you sure you want to ${newStatus.toLowerCase()} the post "${post.title}"?`,
      onOk: () => {
        const updatedPosts = posts.map((p) =>
          p.id === postId ? { ...p, status: newStatus } : p
        );
        setPosts(updatedPosts);
        setFilteredPosts(updatedPosts);
        message.success(`Post "${post.title}" ${newStatus.toLowerCase()}d successfully`);
      },
    });
  };

  const handleViewPost = (post) => {
    const slug = post.title.toLowerCase().replace(/\s+/g, '-');
    const category = post.tags[0];
    const frontendUrl = `/pages/News/${category}/${slug}`;
    window.open(frontendUrl, '_blank');
  };

  const handleAddPostSubmit = (values) => {
    const newPost = {
      id: posts.length + 1,
      title: values.title,
      date: values.date.format('YYYY-MM-DD'),
      description: values.description,
      tags: values.tags,
      image: values.image,
      views: values.views || 0,
      status: 'Active',
    };
    const updatedPosts = [...posts, newPost];
    setPosts(updatedPosts);
    setFilteredPosts(updatedPosts);
    setIsAddModalVisible(false);
    form.resetFields();
    message.success(`Post "${newPost.title}" added successfully`);
  };

  const handleEditPostSubmit = (values) => {
    const updatedPosts = posts.map((p) =>
      p.id === selectedPost.id ? { ...p, ...values, date: values.date.format('YYYY-MM-DD') } : p
    );
    setPosts(updatedPosts);
    setFilteredPosts(updatedPosts);
    setIsEditModalVisible(false);
    form.resetFields();
    message.success(`Post "${values.title}" updated successfully`);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field && sorter.order) {
      const sorted = [...filteredPosts].sort((a, b) => {
        const valueA = a[sorter.field].toString().toLowerCase();
        const valueB = b[sorter.field].toString().toLowerCase();
        return sorter.order === 'ascend' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
      setFilteredPosts(sorted);
    }
  };

  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title', sorter: true },
    { title: 'Date', dataIndex: 'date', key: 'date', sorter: true },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => tags.join(', '),
    },
    { title: 'Views', dataIndex: 'views', key: 'views', sorter: true },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: true },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => handleViewPost(record)}>View</Button>
          <Button onClick={() => handleEditPost(record)}>Edit</Button>
          <Button
            danger={record.status === 'Active'}
            onClick={() => handleToggleStatus(record.id)}
          >
            {record.status === 'Active' ? 'Deactivate' : 'Activate'}
          </Button>
          <Button danger onClick={() => handleDeletePost(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder="Search posts by title or description"
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Button type="primary" onClick={handleAddPost}>
          Add Post
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredPosts}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filteredPosts.length,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />

      <Modal
        title="Add New Post"
        open={isAddModalVisible} // Changed from 'visible' for Ant Design v5 compatibility
        onCancel={() => {
          setIsAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleAddPostSubmit}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter the post title' }]}
          >
            <Input placeholder="Enter post title" />
          </Form.Item>
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select the post date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea placeholder="Enter post description" rows={4} />
          </Form.Item>
          <Form.Item
            name="tags"
            label="Tags"
            rules={[{ required: true, message: 'Please select at least one tag' }]}
          >
            <Select mode="multiple" placeholder="Select tags">
              {tagOptions.map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="image"
            label="Image URL"
            rules={[{ required: true, message: 'Please enter the image URL' }]}
          >
            <Input placeholder="Enter image URL" />
          </Form.Item>
          <Form.Item name="views" label="Views" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Post
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsAddModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Edit Post: ${selectedPost?.title || ''}`}
        open={isEditModalVisible} // Changed from 'visible'
        onCancel={() => {
          setIsEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleEditPostSubmit}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter the post title' }]}
          >
            <Input placeholder="Enter post title" />
          </Form.Item>
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select the post date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea placeholder="Enter post description" rows={4} />
          </Form.Item>
          <Form.Item
            name="tags"
            label="Tags"
            rules={[{ required: true, message: 'Please select at least one tag' }]}
          >
            <Select mode="multiple" placeholder="Select tags">
              {tagOptions.map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="image"
            label="Image URL"
            rules={[{ required: true, message: 'Please enter the image URL' }]}
          >
            <Input placeholder="Enter image URL" />
          </Form.Item>
          <Form.Item name="views" label="Views">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsEditModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Posts;